import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  IconButton,
  Button,
  Checkbox,
  makeStyles,
  Snackbar,
  CircularProgress,
  Chip,
} from "@material-ui/core";
// Import common styles
import { inputFieldStyles, commonInlineStyles } from "../../styles";
import MuiAlert from "@material-ui/lab/Alert";
import DeleteIcon from "@material-ui/icons/Delete";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
// Import axios for API calls
import axios from "axios";
import { appUrl } from "../../../../../../../utils/config";
import { useIsMounted } from "../../../../../hooks/useIsMounted";
import { useLocation } from "react-router-dom";



// Create Alert component for Snackbar
const Alert = (props) => {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "0 2px",
    overflow: "hidden",
  },
  button: {
    minWidth: 180,
    // fontWeight: 600,
    // fontSize: 16,
    borderRadius: 30,
    // height: 44,
  },
  tableContainer: {
    overflowX: "auto",
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: "8px",
      height: "8px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#f1f1f1",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#888",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#555",
    },
    border: "1px solid #c6c6c6",
  },
  table: {
    minWidth: 650,
    width: "100%",
    tableLayout: "fixed",
    border: "0px solid #c6c6c6 !important",

    "@media (max-width: 960px)": {
      minWidth: 1200, // Ensure horizontal scroll on smaller screens
    },
  },
  tableCell: {
    padding: theme.spacing(1),
    minWidth: 150, // Minimum width for cells
    "& .MuiSelect-root": {
      width: "100%",
      backgroundColor: "#fff",
    },
  },
  required: {
    color: "red",
    marginLeft: theme.spacing(0.5),
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
  },
  proceedButton: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  backButton: {
    marginRight: theme.spacing(2),
  },
  saveButton: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));

/**
 * DocumentDeliverables Component
 *
 * This component handles the configuration of document deliverables for pharmaceutical products.
 * It allows users to specify document types, formats, statuses, and responsibilities.
 * Features include:
 *  - Document type configuration with format selection
 *  - Status specification (start and end)
 *  - Organization responsibility assignment
 *  - Mandatory document flagging
 *
 * The component handles loading configuration data from APIs and saving selections back to the backend.
 * It implements validation to ensure proper configuration before proceeding to the next step.
 */
const DocumentDeliverables = ({
  formData,
  updateStepData,
  classes: parentClasses,
  readOnly = false,
  handleNext,
  handleBack,
  handleSubmit,
  activeStep,
  stepsLength,
  isDocumentDeliverablesEditMode = false,
  originalFormData,
  onValidationChange = () => {},
  markApiCallSuccessful, // Function to mark API call as successful
  allFgStatuses: propAllFgStatuses, // FG statuses passed from parent (ViewProductEdit)
}) => {
  const classes = useStyles();
  const isMounted = useIsMounted();

  // State variables
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
    key: Date.now(), // Add a key to force re-render
  });
  const [validationErrors, setValidationErrors] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [fileFormatTypes, setFileFormatTypes] = useState([]);
  const [collaboratorTypes, setCollaboratorTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isInitialSaveSuccess, setIsInitialSaveSuccess] = useState(() => {
    const savedValue = sessionStorage.getItem('addedDocumentDeliverables');
    return savedValue === "true";
  });
  const [updateModeData, setUpdateModeData] = useState(null);
  const [emptyDocumentDeliverables, setEmptyDocumentDeliverables] = useState(false);
  const [allFgStatuses, setAllFgStatuses] = useState(null);
  const location = useLocation();
  const editPendingStatus = location.state?.isPending && location.state?.isEdit;
  const selectedProductFormData = location.state?.selectedProductFormData;
  const selectedCollabrixID = location.state?.productDetails?.collabrixProductID;
  const viewOnlyProductDetails = location.state?.viewProductDetails;

  // Add per-row, per-field error state
  const [rowErrors, setRowErrors] = useState([]);
  // Initialize documents state from formData with proper null checks
  const [documents, setDocuments] = useState(() => {
    // Check if we have document deliverables in formData
    if (formData?.documentDeliverables?.length > 0) {
      return formData.documentDeliverables.map(doc => {
        const collaborator = (Array.isArray(collaboratorTypes) ? collaboratorTypes : []).find(
          (c) => c.collaborator_type_id === (doc.orgResponsibleID || doc.uploadOrgCollaboratorTypeID)
        );
        return {
          ...doc,
          documentTypeID: doc.documentTypeID || "",
          fileFormatTypeID: doc.fileFormatTypeID || "",
          startStatus: doc.startStatus || "Not Specified",
          endStatus: doc.endStatus || "Not Specified",
          orgResponsibleID: doc.orgResponsibleID || "",
          mandatory: doc.mandatory || false,
          orgResponsibleName: collaborator?.collaborator_type_name || "",
        };
      });
    } 
    // Check if we have document deliverables in selectedProductFormData
    else if (selectedProductFormData?.documentDeliverables?.length > 0) {
      return selectedProductFormData.documentDeliverables.map(doc => {
        const collaborator = (Array.isArray(collaboratorTypes) ? collaboratorTypes : []).find(
          (c) => c.collaborator_type_id === (doc.orgResponsibleID || doc.uploadOrgCollaboratorTypeID)
        );
        return {
          ...doc,
          documentTypeID: doc.documentTypeID || "",
          fileFormatTypeID: doc.fileFormatTypeID || "",
          startStatus: doc.startStatus || "Not Specified",
          endStatus: doc.endStatus || "Not Specified",
          orgResponsibleID: doc.orgResponsibleID || "",
          mandatory: doc.mandatory || false,
          orgResponsibleName: collaborator?.collaborator_type_name || "",
        };
      });
    }
    // If we're not in view-only or read-only mode, create a default empty row
    else if (!readOnly && !location.state?.viewProductDetails) {
      return [{
        documentTypeID: "",
        fileFormatTypeID: "",
        startStatus: "Not Specified",
        endStatus: "Not Specified",
        orgResponsibleID: "",
        mandatory: false,
      }];
    }
    // Otherwise return an empty array
    return [];
  });


  // Refs
  const initializedRef = useRef(false);
  const isUpdatingRef = useRef(false);
  const isMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const collabrixID = formData?.productDetails?.collabrixProductID

  // API base URL - replace with your actual API base URL
const apiBaseUrl = useMemo(() => {
  const appName = localStorage.getItem("appName");
  const network = localStorage.getItem("network");
  const collabType = localStorage.getItem("collabType");
  const roleName = localStorage.getItem("roleName");

  if (appName && network && collabType && roleName) {
    return `${appUrl}/${appName}/${network}/${collabType}/${roleName}`;
  }
  return null;
}, [
  localStorage.getItem("appName"),
  localStorage.getItem("network"),
  localStorage.getItem("collabType"),
  localStorage.getItem("roleName"),
]);

  // Memoized update function with proper cleanup
  const updateDocuments = useCallback((newDocuments) => {
    if (!isUpdatingRef.current && isMountedRef.current) {
      isUpdatingRef.current = true;
      try {
        setDocuments(newDocuments);
        if (typeof updateStepData === 'function' && isMountedRef.current) {
          updateStepData("documentDeliverables", newDocuments);
        }
      } catch (error) {
        console.error("Error updating documents:", error);
      } finally {
        isUpdatingRef.current = false;
      }
    }
  }, [updateStepData]);

  // Fetch data only once on mount with proper cleanup
  useEffect(() => {
    let isMounted = true;

    const fetchFGStatuses = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/fetchFGStatuses/true`);
        if (isMounted && response?.data?.data) {
          setAllFgStatuses(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching FG statuses:", error);
      }
    };

    const fetchAllData = async () => {
      if (!isMounted) return;

      try {
        setIsLoading(true);
        setError(null);
      
        // Declare result variables upfront
        let docTypesResult = { status: "fulfilled", value: { data: { data: { documentTypesList: [] } } } };
        let formatTypesResult = { status: "fulfilled", value: { data: { data: { fileFormatTypesList: [] } } } };
        let collaboratorTypesResult = { status: "fulfilled", value: { data: { data: [] } } };
      
        if (!readOnly || !isDocumentDeliverablesEditMode) {
          // Fetch FG statuses for dropdown options
          await fetchFGStatuses();
          
          // Only fetch data if not in read-only mode
          const results = await Promise.allSettled([
            axios.get(`${apiBaseUrl}/fetchDocumentTypes/true`),
            axios.get(`${apiBaseUrl}/fetchFileFormatTypes/true`),
            axios.get(
              `${appUrl}/admin/${
                localStorage.collabType === "CMO" ? "fetchNetworkTypes" : "fetchPartnerTypes"
              }/${localStorage.ecosystemId}`
            ),
          ]);
      
          [docTypesResult, formatTypesResult, collaboratorTypesResult] = results;
        } else {
          // Even in read-only mode, we need FG statuses for display
          await fetchFGStatuses();
        }
      
        // Check if each result is fulfilled and set state accordingly
        if (
          docTypesResult.status === "fulfilled" &&
          docTypesResult.value?.data?.data?.documentTypesList
        ) {
          setDocumentTypes(docTypesResult.value.data.data.documentTypesList);
        }
      
        if (
          formatTypesResult.status === "fulfilled" &&
          formatTypesResult.value?.data?.data?.fileFormatTypesList
        ) {
          setFileFormatTypes(formatTypesResult.value.data.data.fileFormatTypesList);
        }
      
        if (
          collaboratorTypesResult.status === "fulfilled" &&
          collaboratorTypesResult.value?.data?.data
        ) {
          setCollaboratorTypes(collaboratorTypesResult.value.data.data);
        }
      
        if (
          docTypesResult.status === "rejected" ||
          formatTypesResult.status === "rejected" ||
          collaboratorTypesResult.status === "rejected"
        ) {
          setError("Some configuration data failed to load.");
        }
      } catch (error) {
        if (isMounted) {
          setError("Failed to load configuration data. Please try again later.");
        }
      } finally {
        if (isMounted) {
          // Defensive check: if documents is empty after loading, add default row (unless readOnly/viewOnly)
          if (
            (!documents || documents.length === 0) &&
            !readOnly &&
            !viewOnlyProductDetails
          ) {
            const defaultRow = {
              documentTypeID: "",
              fileFormatTypeID: "",
              startStatus: "Not Specified",
              endStatus: "Not Specified",
              orgResponsibleID: "",
              mandatory: false,
            };
            updateDocuments([defaultRow]);
          }
          setIsLoading(false);
        }
      }
    };

    fetchAllData();

    return () => {
      isMounted = false;
    };
  }, [isDocumentDeliverablesEditMode]); // Empty dependency array - only run once on mount

  // Fetch and update activated statuses when in edit mode
  useEffect(() => {
    const collabrixProductID = formData?.productDetails?.collabrixProductID;
    if (editPendingStatus && collabrixProductID) {
      const callData = async () => {
        try {
          const response = await axios.get(
            `${apiBaseUrl}/fetchProductDetails/${collabrixProductID}`
          );
          if (response.data && response.data.status) {
            const apiData = response.data.data;
            setUpdateModeData(apiData)
            const activatedStatuses = apiData.documentDetailsList || []

            if (activatedStatuses.length === 0) {
              setEmptyDocumentDeliverables(true)
            }

            // Map the API response data to match the component's state structure
            const mappedDocuments = activatedStatuses.map(doc => ({
              ...doc,
              documentTypeID: doc.documentTypeID,
              fileFormatTypeID: doc.fileFormatTypeID,
              startStatus: doc.startOfUploadStatusText || doc.startOfUploadStatus || "Not Specified",
              endStatus: doc.endOfUploadStatusText || doc.endOfUploadStatus || "Not Specified",
              orgResponsibleID: doc.uploadOrgCollaboratorTypeID,
              mandatory: doc.documentMandatoryFlag,
              orgResponsibleName: doc.orgResponsibleName || ""
            }));

            // Update stepData with mapped documents
            setDocuments(mappedDocuments);
            if (typeof updateStepData === "function") {
              updateStepData("documentDeliverables", mappedDocuments);
            }
          } else {
            setError("Failed to fetch product details.");
          }
        } catch (error) {
          setError("An error occurred while fetching product details.");
        }
      };
      if(!readOnly) {
        callData();
      }
    }
  }, [editPendingStatus, formData?.productDetails?.collabrixProductID]);

  // Create default rows only once when component mounts or when we know there are no documents
  useEffect(() => {
    // Only create a default row if:
    // 1. We haven't initialized yet
    // 2. Component is mounted
    // 3. We don't have documents
    // 4. Not in view-only mode
    // 5. Not in read-only mode
    if (
      !initializedRef.current &&
      isMountedRef.current &&
      (!documents || documents.length === 0) &&
      !viewOnlyProductDetails && 
      !readOnly
    ) {
      const defaultRow = {
        documentTypeID: "",
        fileFormatTypeID: "",
        startStatus: "Not Specified",
        endStatus: "Not Specified",
        orgResponsibleID: "",
        mandatory: false,
      };

      updateDocuments([defaultRow]);
      initializedRef.current = true;
    }
  }, [documents, updateDocuments, viewOnlyProductDetails, readOnly]);

  // Memoized handlers with proper cleanup
  const handleDocumentChange = (index, field, value) => {
    if (!isMountedRef.current) return;


    const newDocuments = [...documents];
    const updatedDoc = { ...newDocuments[index] };

    if (field === "orgResponsibleID") {
      const selectedOrg = collaboratorTypes.find(
        (c) => c.collaborator_type_id === value
      );
      updatedDoc[field] = value;
      updatedDoc.orgResponsibleName = selectedOrg?.collaborator_type_name || "";
    } else {
      updatedDoc[field] = value;
    }

    newDocuments[index] = updatedDoc;
    updateDocuments(newDocuments);

    // Clear error for this field in rowErrors as soon as it is filled/changed
    setRowErrors(prev => {
      const updated = [...prev];
      if (!updated[index]) updated[index] = {};
      delete updated[index][field];
      return updated;
    });
  };



  const handleAddRow = useCallback(() => {
    if (!isMountedRef.current) return;
    if (documents.length > 0) {
      const lastDoc = documents[documents.length - 1];
      if (!lastDoc.documentTypeID) {
        setSnackbar({
          open: true,
          message: "Please select a Document Type in the current row before adding a new row.",
          severity: "error",
        });
        setRowErrors(prev => {
          const updated = [...prev];
          updated[documents.length - 1] = {
            ...updated[documents.length - 1],
            documentTypeID: "Please select a Document Type"
          };
          return updated;
        });
        return;
      }
    }
    const newDocuments = [
      ...documents,
      {
        documentTypeID: "",
        fileFormatTypeID: "",
        startStatus: "Not Specified",
        endStatus: "Not Specified",
        orgResponsibleID: "",
        mandatory: false,
      },
    ];
    updateDocuments(newDocuments);
    setRowErrors(prev => [...prev, {}]);
  }, [documents, updateDocuments]);

  const handleDeleteRow = useCallback((index) => {
    if (!isMountedRef.current) return;

    const newDocuments = documents.filter((_, i) => i !== index);
    updateDocuments(newDocuments);
  }, [documents, updateDocuments]);

  // Show error in snackbar if API calls fail
  useEffect(() => {
    if (error && isMountedRef.current) {
      setSnackbar({
        open: true,
        message: error,
        severity: "error",
      });
    }
  }, [error]);

  // Memoize availableStatuses to prevent re-creation on every render
  const availableStatuses = useMemo(() => {
    // PO statuses: From formData.fgStatuses (Status Selection step)
    const poStatuses = formData?.fgStatuses?.poStatuses || [];
    
    // Batch statuses: From allFgStatuses/propAllFgStatuses (fetchfgstatuses API)
    const apiStatuses = propAllFgStatuses || allFgStatuses;
    const batchStatuses = apiStatuses?.batchStatuses || [];
    
    
    
    if (!poStatuses.length && !batchStatuses.length) {
      return [];
    }
    
    // For PO statuses: only show selected ones (with value or activated true)
    const filteredPoStatuses = poStatuses.filter(status => status.value || status.activated);
    
    // For batch statuses: show all activated ones (since they're not selectable in Status Selection)
    const filteredBatchStatuses = batchStatuses.filter(status => status.activated);
    
  
    
    const allStatuses = [...filteredPoStatuses, ...filteredBatchStatuses];
    
    
    return Array.from(
      new Map(allStatuses.map(s => [s.status_name, s])).values()
    );
  }, [formData?.fgStatuses, allFgStatuses, propAllFgStatuses]);

  // Helper function to check if a status is valid (considering PO vs batch status rules)
  const isStatusValid = (statusName) => {
    if (!statusName || statusName === "Not Specified") return true;

    // Get all status collections
    const apiStatuses = propAllFgStatuses || allFgStatuses;
    const poStatuses = formData?.fgStatuses?.poStatuses || [];
    const allBatchStatuses = apiStatuses?.batchStatuses || [];

    // First check if it's a PO status (check in formData.fgStatuses.poStatuses)
    const isPOStatus = poStatuses.some(status => {
      const statusNameMatch = String(status.status_name).trim().toLowerCase() === String(statusName).trim().toLowerCase();
      const statusIdMatch = String(status.status_id) === String(statusName);
      return statusNameMatch || statusIdMatch;
    });

    if (isPOStatus) {
      // For PO statuses: must be in availableStatuses (activated)
      return availableStatuses.some(status => {
        const statusNameMatch = String(status.status_name).trim().toLowerCase() === String(statusName).trim().toLowerCase();
        const statusIdMatch = String(status.status_id) === String(statusName);
        return statusNameMatch || statusIdMatch;
      });
    }

    // Check if it's a batch status (check in allFgStatuses.batchStatuses)
    const isBatchStatus = allBatchStatuses.some(status => {
      const statusNameMatch = String(status.status_name).trim().toLowerCase() === String(statusName).trim().toLowerCase();
      const statusIdMatch = String(status.status_id) === String(statusName);
      return statusNameMatch || statusIdMatch;
    });

    if (isBatchStatus) {
      // For batch statuses: always valid if it exists in the master list (not toggleable)
      return true;
    }

    // If it's neither PO nor batch status, check in availableStatuses as fallback
    return availableStatuses.some(status => {
      const statusNameMatch = String(status.status_name).trim().toLowerCase() === String(statusName).trim().toLowerCase();
      const statusIdMatch = String(status.status_id) === String(statusName);
      return statusNameMatch || statusIdMatch;
    });
  };

  // Only update rowErrors when documents or availableStatuses change
  useEffect(() => {
    // Don't run validation if status data is not loaded yet
    if (!availableStatuses.length || (!allFgStatuses && !propAllFgStatuses)) return;
    
    if (documents.length > 0) {
      let hasErrors = false;
      const newRowErrors = documents.map((doc, index) => {
        const errors = {};
        // Check start status using the new validation logic
        if (doc.startStatus && doc.startStatus !== "Not Specified") {
          if (!isStatusValid(doc.startStatus)) {
            errors.startStatus = "Please select a valid status.";
            hasErrors = true;
          }
        }
        // Check end status using the new validation logic
        if (doc.endStatus && doc.endStatus !== "Not Specified") {
          if (!isStatusValid(doc.endStatus)) {
            errors.endStatus = "Please select a valid status.";
            hasErrors = true;
          }
        }
        return errors;
      });
      // Only set if different
      if (JSON.stringify(newRowErrors) !== JSON.stringify(rowErrors)) {
        setRowErrors(newRowErrors);
      }
      // Only show the toaster if there is at least one error in newRowErrors
      const hasAnyError = newRowErrors.some(row => Object.keys(row).length > 0);
      if (hasAnyError) {
        setSnackbar({
          open: true,
          message: "Some document statuses are no longer available. Please update them.",
          severity: "error",
        });
      }
    }
  }, [availableStatuses, documents, allFgStatuses, propAllFgStatuses, formData?.fgStatuses]); // NOT rowErrors

  // Only call onValidationChange when documents or rowErrors change
  useEffect(() => {
    onValidationChange(validate());
  }, [documents, rowErrors, onValidationChange]);

  // Update validateDocumentDeliverables to include status validation
  const validateDocumentDeliverables = () => {
    const errors = [];
    const newRowErrors = documents.map(() => ({}));
    let firstError = null;

    if (!documents || documents.length === 0) {
      errors.push("Please add at least one document deliverable.");
    }

    documents.forEach((doc, index) => {
      if (!doc.documentTypeID) {
        errors.push(`Row ${index + 1}: Please select a Document Type`);
        newRowErrors[index].documentTypeID = "Please select a Document Type";
        if (!firstError) firstError = { row: index, field: "documentTypeID" };
      }
      if (!doc.orgResponsibleID) {
        errors.push(`Row ${index + 1}: Please select an Organization Responsible`);
        newRowErrors[index].orgResponsibleID = "Please select an Organization Responsible";
        if (!firstError) firstError = { row: index, field: "orgResponsibleID" };
      }
      if (!doc.fileFormatTypeID) {
        errors.push(`Row ${index + 1}: Please select a Format`);
        newRowErrors[index].fileFormatTypeID = "Please select a Format";
        if (!firstError) firstError = { row: index, field: "fileFormatTypeID" };
      }

      // Validate start status using the new validation logic
      if (doc.startStatus && doc.startStatus !== "Not Specified") {
        if (!isStatusValid(doc.startStatus)) {
          errors.push(`Row ${index + 1}: Please select a valid status.`);
          newRowErrors[index].startStatus = "Please select a valid status.";
          if (!firstError) firstError = { row: index, field: "startStatus" };
        }
      }

      // Validate end status using the new validation logic
      if (doc.endStatus && doc.endStatus !== "Not Specified") {
        if (!isStatusValid(doc.endStatus)) {
          errors.push(`Row ${index + 1}: Please select a valid status.`);
          newRowErrors[index].endStatus = "Please select a valid status.";
          if (!firstError) firstError = { row: index, field: "endStatus" };
        }
      }
    });

    setValidationErrors(errors);
    setRowErrors(newRowErrors);

    if (errors.length > 0) {
      setSnackbar({
        open: true,
        message: "Please fix the validation errors below.",
        severity: "error",
      });
      return false;
    }

    return true;
  };

    // Helper to get status_id by status_name from fgStatuses
  const getStatusId = (statusName) => {
    // Return 0 for empty or invalid status names
    if (!statusName || statusName === "Not Specified") {
      return 0;
    }

    // PO statuses: From formData.fgStatuses (Status Selection step)
    const poStatuses = formData?.fgStatuses?.poStatuses || [];
    
    // Batch statuses: From allFgStatuses/propAllFgStatuses (fetchfgstatuses API)
    const apiStatuses = propAllFgStatuses || allFgStatuses;
    const batchStatuses = apiStatuses?.batchStatuses || [];
    
    const allStatuses = [...poStatuses, ...batchStatuses];
    
    // First try exact match
    let found = allStatuses.find(s => s.status_name === statusName);
    
    // If no exact match, try case-insensitive match
    if (!found) {
      found = allStatuses.find(s => 
        s.status_name && 
        s.status_name.toLowerCase().trim() === statusName.toLowerCase().trim()
      );
    }
    
    return found ? found.status_id : 0;
  };
  
    const saveDocumentDeliverablesToAPI = async () => {
    try {
      if (isMountedRef.current) setIsLoading(true);
      const payload = {
        vendorMosymphonyID: updateModeData?.productDetails?.vendorMosymphonyID || formData.productDetails?.vendorMosymphonyID,
        collabrixProductID: updateModeData?.productDetails?.collabrixProductID || collabrixID,
        documentDetailsList: documents.map((doc) => {
          const documentTypeObj = documentTypes.find(
            (type) => type.documentTypeID === doc.documentTypeID
          );
          const documentTypeID = documentTypeObj?.documentTypeID || null;

          const fileFormatObj = fileFormatTypes.find(
            (format) => format.fileFormatTypeID === doc.fileFormatTypeID
          );
          const fileFormatTypeID = fileFormatObj?.fileFormatTypeID || null;

          // Get status IDs if not 'Not Specified'
          const startOfUploadStatusId = doc.startStatus && doc.startStatus !== "Not Specified"
            ? getStatusId(doc.startStatus)
            : null;
          const endOfUploadStatusId = doc.endStatus && doc.endStatus !== "Not Specified"
            ? getStatusId(doc.endStatus)
            : null;

          return {
            documentTypeID,
            fileFormatTypeID,
            startOfUploadStatus: doc.startStatus === "Not Specified"
              ? "Not Specified"
              : String(getStatusId(doc.startStatus)),
            endOfUploadStatus: doc.endStatus === "Not Specified"
              ? "Not Specified"
              : String(getStatusId(doc.endStatus)),
            uploadOrgCollaboratorTypeID: doc.orgResponsibleID,
            documentMandatoryFlag: doc.mandatory,
            orgResponsibleName: doc.orgResponsibleName,
          };
        }),
      };

      // Robust normalization for change detection
      function normalizeDocs(arr) {
        return (arr || []).map(doc => ({
          documentTypeID: String(doc.documentTypeID ?? ''),
          fileFormatTypeID: String(doc.fileFormatTypeID ?? ''),
          startOfUploadStatus: String(doc.startOfUploadStatus ?? doc.startStatus ?? 'Not Specified'),
          endOfUploadStatus: String(doc.endOfUploadStatus ?? doc.endStatus ?? 'Not Specified'),
          uploadOrgCollaboratorTypeID: String(doc.uploadOrgCollaboratorTypeID ?? doc.orgResponsibleID ?? ''),
          documentMandatoryFlag: Boolean(doc.documentMandatoryFlag ?? doc.mandatory ?? false),
          // orgResponsibleName intentionally omitted from comparison
        })).sort((a, b) => {
          // Sort by documentTypeID, then fileFormatTypeID for stability
          if (a.documentTypeID !== b.documentTypeID) return a.documentTypeID.localeCompare(b.documentTypeID);
          return a.fileFormatTypeID.localeCompare(b.fileFormatTypeID);
        });
      }

      let response;
      if (isInitialSaveSuccess || (editPendingStatus && !emptyDocumentDeliverables)) {
        // Helper function to format productDetails for API
        const formatProductDetailsForAPI = (productDetails) => {
          const formatted = { ...productDetails };
          
          // Convert receivingStorageLocationIDsFromSponsorERP from array to comma-separated string
          if (formatted.receivingStorageLocationIDsFromSponsorERP) {
            if (Array.isArray(formatted.receivingStorageLocationIDsFromSponsorERP)) {
              formatted.receivingStorageLocationIDsFromSponsorERP = 
                formatted.receivingStorageLocationIDsFromSponsorERP.join(",");
            }
          }
          
          return formatted;
        };

        // Build update-specific payload
        const updatePayload = {
          ...payload,
          productDetails: formatProductDetailsForAPI(updateModeData?.productDetails || formData.productDetails),
          updateDocumentDeliverablesFlag: true,
        };

        delete updatePayload.vendorMosymphonyID;
        delete updatePayload.collabrixProductID;

        const normalizedNew = normalizeDocs(payload.documentDetailsList);
        let normalizedExisting;
        
        if (editPendingStatus && updateModeData?.documentDetailsList) {
          normalizedExisting = normalizeDocs(updateModeData.documentDetailsList);
        } else if (location.state?.addNewProductConfig && location.state?.selectedProductFormData?.documentDeliverables) {
          normalizedExisting = normalizeDocs(location.state.selectedProductFormData.documentDeliverables);
        } else if (formData.documentDeliverables) {
          normalizedExisting = normalizeDocs(formData.documentDeliverables);
        } else {
          normalizedExisting = [];
        }
        
                  const hasChanges = JSON.stringify(normalizedNew) !== JSON.stringify(normalizedExisting);
        
        if (!hasChanges) {
          
          handleNext();
          return true;
        }

          
        response = await axios.post(
          `${apiBaseUrl}/updateProductDetails`,
          updatePayload
        );
        
        // Mark API call as successful
        if (typeof markApiCallSuccessful === 'function' && response.data && response.data.status) {
          
          markApiCallSuccessful(); // This now handles both showing success message and navigation
          return true;
        }
              } else {        
          response = await axios.post(
          `${apiBaseUrl}/addProductDocumentDeliverables`,
          payload
        );
        
        // Mark API call as successful
        if (typeof markApiCallSuccessful === 'function' && response.data && response.data.status) {
          // If initial save is successful, set the flag to true
          setIsInitialSaveSuccess(true);
          // Store in sessionStorage
          sessionStorage.setItem(`addedDocumentDeliverables`, "true");
          
          
          markApiCallSuccessful(); // This now handles both showing success message and navigation
          return true;
        }
        if (response.data && response.data.status) {          
          setIsInitialSaveSuccess(true);
          // Store in sessionStorage
          sessionStorage.setItem(`addedDocumentDeliverables`, "true");
        }
      } 
      
      // handleNext is now called inside the setTimeout after markApiCallSuccessful
      if (isMountedRef.current) {
        setSnackbar({
          open: true,
          message: "Failed to save document deliverables. Please try again.",
          severity: "error",
        });
      }
    } catch (error) {
      if (isMountedRef.current) {
        setSnackbar({
          open: true,
          message:
            "Error saving document deliverables: " +
            (error.message || "Unknown error"),
          severity: "error",
        });
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  const handleNextWithSave = async () => {
    const isValid = validateDocumentDeliverables();
    if (!isValid) return;

    const success = await saveDocumentDeliverablesToAPI();
    if (!success) {
      setSnackbar({
        open: true,
        message: "Failed to save document deliverables. Please try again.",
        severity: "error",
      });
    }
  };

  // Handle closing snackbar
  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Sync documents state with formData.documentDeliverables when it changes
  useEffect(() => {
    if (formData?.documentDeliverables?.length > 0) {
      setDocuments(formData.documentDeliverables.map(doc => {
        const collaborator = (Array.isArray(collaboratorTypes) ? collaboratorTypes : []).find(
          (c) => c.collaborator_type_id === (doc.orgResponsibleID || doc.uploadOrgCollaboratorTypeID)
        );
        return {
          ...doc,
          documentTypeID: doc.documentTypeID || "",
          fileFormatTypeID: doc.fileFormatTypeID || "",
          startStatus: doc.startStatus || "Not Specified",
          endStatus: doc.endStatus || "Not Specified",
          orgResponsibleID: doc.orgResponsibleID || "",
          mandatory: doc.mandatory || false,
          orgResponsibleName: collaborator?.collaborator_type_name || "",
        };
      }));
    }
  }, [formData?.documentDeliverables, collaboratorTypes]);

  // Validation logic
  const validate = () => {
    // At least one document
    if (!documents || documents.length === 0) return false;
    // No row errors and all required fields filled
    for (let i = 0; i < documents.length; i++) {
      const doc = documents[i];
      if (
        !doc.documentTypeID ||
        !doc.fileFormatTypeID ||
        !doc.orgResponsibleID ||
        (rowErrors[i] && Object.keys(rowErrors[i]).length > 0)
      ) {
        return false;
      }
    }
    return true;
  };



  return (
    <>
      {/* Full Page Loader */}
      {isLoading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 9999,
          }}
        >
          <CircularProgress color="primary" thickness={7} />
        </div>
      )}


      {/* Document Types Configuration */}
      <form className=" stepper-table documents-types-table">
        <Paper elevation={0} className={classes.root}>
          <Typography variant="h6">Document Deliverables</Typography>
          <Typography variant="h5">
            Document types {!readOnly && <span className={classes.required}>*</span>}
          </Typography>
          <TableContainer className={classes.tableContainer}>
            <Table className={classes.table} stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell className="document-type-col">
                    Document Type
                  </TableCell>
                  <TableCell className="format-col">Format</TableCell>
                  <TableCell className="upload-status-col">
                    Start Of Upload Status
                  </TableCell>
                  <TableCell className="end-upload-status-col">
                    End Of Upload Status
                  </TableCell>
                  <TableCell className="org-doc-col">
                    Org. Responsible For Document
                  </TableCell>
                  <TableCell className="mandatory-docs-col" align="center">
                    Mandatory Document
                  </TableCell>
                  {/* {!readOnly && (
                  <TableCell align="center" className="actions-col">
                    Actions
                  </TableCell>
                )} */}
                </TableRow>
              </TableHead>
              <TableBody>
                {readOnly && documents.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography>No documents are selected</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {documents.map((doc, index) => (
                  <TableRow key={index}>
                    <TableCell className={classes.tableCell}>
                      {!readOnly ? (
                        <>
                          <Select
                            id={`documentType-${index}`}
                            aria-label={`Document type for row ${index + 1}`}
                            value={doc.documentTypeID || ""}
                            fullWidth
                            onChange={(e) =>
                              handleDocumentChange(
                                index,
                                "documentTypeID",
                                e.target.value
                              )
                            }
                            variant="outlined"
                            style={{ ...commonInlineStyles.tableSelect, outlineColor: rowErrors[index]?.documentTypeID ? 'red' : undefined, outlineWidth: rowErrors[index]?.documentTypeID ? 1 : undefined, outlineStyle: rowErrors[index]?.documentTypeID ? 'solid' : undefined }}
                            inputProps={commonInlineStyles.tableInputProps}
                            displayEmpty 
                            renderValue={(selected) => {
                              if (!selected) {
                                return isLoading
                                  ? "Loading..."
                                  : "Select from the list";
                              }
                              const found = documentTypes.find(dt => dt.documentTypeID === selected);
                              return found ? (found.newDocumentTypeName || found.documentTypeName) : selected;
                            }}
                            MenuProps={{
                              anchorOrigin: {
                                vertical: "bottom",
                                horizontal: "left",
                              },
                              transformOrigin: {
                                vertical: "top",
                                horizontal: "left",
                              },
                              getContentAnchorEl: null,
                            }}
                          >
                            {documentTypes && documentTypes.length > 0 ? (
                              documentTypes
                                .filter(typeObj => typeObj && typeObj.documentTypeName && typeObj.activated)
                                .filter((typeObj) => {
                                  const usedTypes = documents
                                    .filter((_, i) => i !== index)
                                    .map((d) => d.documentTypeID)
                                    .filter((t) => t !== "");
                                  return (
                                    !usedTypes.includes(typeObj.documentTypeID) ||
                                    doc.documentTypeID === typeObj.documentTypeID
                                  );
                                })
                                .map((typeObj) => (
                                  <MenuItem
                                    key={typeObj.documentTypeID}
                                    value={typeObj.documentTypeID}
                                  >
                                    {typeObj.documentTypeName}
                                  </MenuItem>
                                ))
                            ) : (
                              <MenuItem disabled>
                                No document types available
                              </MenuItem>
                            )}
                          </Select>
                          {rowErrors[index]?.documentTypeID && (
                            <Typography color="error" variant="body2" className="error-message">
                              {rowErrors[index].documentTypeID}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography style={{fontSize: 14}}>{documentTypes.find(dt => dt.documentTypeID === doc.documentTypeID)?.newDocumentTypeName || documentTypes.find(dt => dt.documentTypeID === doc.documentTypeID)?.documentTypeName || doc.documentType}</Typography>
                      )}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {!readOnly ? (
                        <>
                          <Select
                            id={`format-${index}`}
                            aria-label={`Document format for row ${index + 1}`}
                            value={doc.fileFormatTypeID || ""}
                            onChange={(e) =>
                              handleDocumentChange(index, "fileFormatTypeID", e.target.value)
                            }
                            fullWidth
                            variant="outlined"
                            displayEmpty 
                            style={{ ...commonInlineStyles.tableSelect, outlineColor: rowErrors[index]?.fileFormatTypeID ? 'red' : undefined, outlineWidth: rowErrors[index]?.fileFormatTypeID ? 1 : undefined, outlineStyle: rowErrors[index]?.fileFormatTypeID ? 'solid' : undefined }}
                            inputProps={commonInlineStyles.tableInputProps}
                            renderValue={(selected) => {
                              if (!selected) {
                                return isLoading ? "Loading..." : "Select from the list";
                              }
                              const found = fileFormatTypes.find(f => f.fileFormatTypeID === selected);
                              return found ? found.fileFormatTypeName : selected;
                            }}
                            MenuProps={{
                              anchorOrigin: {
                                vertical: "bottom",
                                horizontal: "left",
                              },
                              transformOrigin: {
                                vertical: "top",
                                horizontal: "left",
                              },
                              getContentAnchorEl: null,
                            }}
                          >
                            {fileFormatTypes && fileFormatTypes.length > 0 ? (
                              fileFormatTypes
                                .filter(format => format && format.fileFormatTypeName && format.activated)
                                .map((format) => (
                                  <MenuItem
                                    key={format.fileFormatTypeID}
                                    value={format.fileFormatTypeID}
                                  >
                                    {format.fileFormatTypeName}
                                  </MenuItem>
                                ))
                            ) : (
                              <MenuItem disabled>
                                No format types available
                              </MenuItem>
                            )}
                          </Select>
                          {rowErrors[index]?.fileFormatTypeID && (
                            <Typography color="error" variant="body2" className="error-message">
                              {rowErrors[index].fileFormatTypeID}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography style={{fontSize: 14}}>{fileFormatTypes.find(f => f.fileFormatTypeID === doc.fileFormatTypeID)?.fileFormatTypeName || doc.fileFormatType}</Typography>
                      )}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {!readOnly ? (
                        <Select
                          value={doc.startStatus || "Not Specified"}
                          onChange={(e) =>
                            handleDocumentChange(
                              index,
                              "startStatus",
                              e.target.value
                            )
                          }
                          fullWidth
                          variant="outlined"
                          style={{
                            ...commonInlineStyles.tableSelect,
                            outlineColor: rowErrors[index]?.startStatus ? 'red' : undefined,
                            outlineWidth: rowErrors[index]?.startStatus ? 1 : undefined,
                            outlineStyle: rowErrors[index]?.startStatus ? 'solid' : undefined
                          }}
                          inputProps={commonInlineStyles.tableInputProps}
                          displayEmpty 
                          renderValue={(selected) => {
                            if (!selected) {
                              return isLoading
                                ? "Loading..."
                                : "Select from the list";
                            }
                            return selected || "";
                          }}
                          MenuProps={{
                            anchorOrigin: { vertical: "bottom", horizontal: "left" },
                            transformOrigin: { vertical: "top", horizontal: "left" },
                            getContentAnchorEl: null,
                          }}
                        >
                          <MenuItem value="Not Specified">Not Specified</MenuItem>
                          {availableStatuses.length > 0 ? (
                            availableStatuses.map((status) => (
                              <MenuItem key={status.status_id} value={status.status_name}>
                                {status.status_name}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem disabled>No statuses available</MenuItem>
                          )}
                        </Select>
                      ) : (
                        <Typography style={{fontSize: 14}}>
                          {(() => {
                            const status = availableStatuses.find(
                              s => String(s.status_id) === String(doc.startStatus) || s.status_name === doc.startStatus
                            );
                            if (status) return status.status_name;
                            if (doc.startStatus === "Not Specified") return "Not Specified";
                            return doc.startStatus || "N/A";
                          })()}
                        </Typography>
                      )}
                      {rowErrors[index]?.startStatus && (
                        <Typography color="error" variant="body2" className="error-message">
                          {rowErrors[index].startStatus}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {!readOnly ? (
                        <Select
                          value={doc.endStatus || "Not Specified"}
                          displayEmpty
                          onChange={(e) =>
                            handleDocumentChange(
                              index,
                              "endStatus",
                              e.target.value
                            )
                          }
                          fullWidth
                          variant="outlined"
                          style={{
                            ...commonInlineStyles.tableSelect,
                            outlineColor: rowErrors[index]?.endStatus ? 'red' : undefined,
                            outlineWidth: rowErrors[index]?.endStatus ? 1 : undefined,
                            outlineStyle: rowErrors[index]?.endStatus ? 'solid' : undefined
                          }}
                          inputProps={commonInlineStyles.tableInputProps}
                          renderValue={(selected) => {
                            if (!selected) {
                              return isLoading
                                ? "Loading..."
                                : "Select from the list";
                            }
                            return selected || "";
                          }}
                          MenuProps={{
                            anchorOrigin: { vertical: "bottom", horizontal: "left" },
                            transformOrigin: { vertical: "top", horizontal: "left" },
                            getContentAnchorEl: null,
                          }}
                        >
                          <MenuItem value="Not Specified">Not Specified</MenuItem>
                          {availableStatuses.length > 0 ? (
                            availableStatuses.map((status) => (
                              <MenuItem key={status.status_id} value={status.status_name}>
                                {status.status_name}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem disabled>No statuses available</MenuItem>
                          )}
                        </Select>
                      ) : (
                        <Typography style={{fontSize: 14}}>
                          {(() => {
                            const status = availableStatuses.find(
                              s => String(s.status_id) === String(doc.endStatus) || s.status_name === doc.endStatus
                            );
                            if (status) return status.status_name;
                            if (doc.endStatus === "Not Specified") return "Not Specified";
                            return doc.endStatus || "N/A";
                          })()}
                        </Typography>
                      )}
                      {rowErrors[index]?.endStatus && (
                        <Typography color="error" variant="body2" className="error-message">
                          {rowErrors[index].endStatus}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {!readOnly ? (
                        <>
                          <Select
                            value={doc.orgResponsibleID || ""}
                            displayEmpty 
                            onChange={(e) =>
                              handleDocumentChange(
                                index,
                                "orgResponsibleID",
                                e.target.value
                              )
                            }
                            fullWidth
                            variant="outlined"
                            style={{ ...commonInlineStyles.tableSelect, outlineColor: rowErrors[index]?.orgResponsibleID ? 'red' : undefined, outlineWidth: rowErrors[index]?.orgResponsibleID ? 1 : undefined, outlineStyle: rowErrors[index]?.orgResponsibleID ? 'solid' : undefined }}
                            inputProps={commonInlineStyles.tableInputProps}
                            renderValue={(selected) => {
                              if (!selected) {
                                return isLoading
                                  ? "Loading..."
                                  : "Select from the list";
                              }
                              const collaborator = collaboratorTypes.find(
                                (c) => c.collaborator_type_id === selected
                              );
                              return collaborator
                                ? collaborator.collaborator_type_name
                                : (selected || "");
                            }}
                            MenuProps={{
                              anchorOrigin: {
                                vertical: "bottom",
                                horizontal: "left",
                              },
                              transformOrigin: {
                                vertical: "top",
                                horizontal: "left",
                              },
                              getContentAnchorEl: null,
                            }}
                          >
                            {collaboratorTypes && collaboratorTypes.length > 0 ? (
                              collaboratorTypes
                                .filter(collaborator => collaborator && collaborator.collaborator_type_id)
                                .map((collaborator) => (
                                  <MenuItem
                                    key={collaborator.collaborator_type_id}
                                    value={collaborator.collaborator_type_id}
                                  >
                                    {collaborator.collaborator_type_name}
                                  </MenuItem>
                                ))
                            ) : (
                              <MenuItem disabled>
                                No collaborator types available
                              </MenuItem>
                            )}
                          </Select>
                          {rowErrors[index]?.orgResponsibleID && (
                            <Typography color="error" variant="body2" className="error-message">
                              {rowErrors[index].orgResponsibleID}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography style={{fontSize: 14}}>
                          {collaboratorTypes.find(
                            (c) => c.collaborator_type_id === doc.orgResponsibleID
                          )?.collaborator_type_name || doc.orgResponsibleID}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell className={`${classes.tableCell} mandatory-docs-col`} align="center">
                      {readOnly ? (
                        doc.mandatory === true ? (
                          <Checkbox
                            checked={true}
                            color="primary"
                            disabled={true}
                          />
                        ) : null
                      ) : (
                        <Checkbox
                          checked={doc.mandatory === true}
                          onChange={(e) =>
                            handleDocumentChange(
                              index,
                              "mandatory",
                              e.target.checked
                            )
                          }
                          color="primary"
                          disabled={readOnly}
                        />
                      )}
                    </TableCell>
                    {/* {!readOnly && (
                    <TableCell align="center">
                      <IconButton onClick={() => handleDeleteRow(index)}>
                        <DeleteIcon color="error" />
                      </IconButton>
                    </TableCell>
                  )} */}
                  </TableRow>
                ))}
              </TableBody>
            </Table>        </TableContainer>

          {!readOnly && (
            <div className="document-types-actions">
              <Button
                variant="outlined"
                color="primary"
                onClick={handleAddRow}
                startIcon={<AddCircleOutlineIcon style={{ fontSize: 21 }} />}
                className="outline-rounded-btn add-new-row-btn"
              >
                Add New Row
              </Button>
            </div>
          )}
        </Paper>

      </form>

      {/* Error Snackbar */}
      <Snackbar
        key={snackbar.key}
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        style={{ zIndex: 9999 }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          style={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Navigation Buttons */}
      {(!readOnly && !viewOnlyProductDetails && !isDocumentDeliverablesEditMode) &&
        <Box
          sx={{
            display: "flex",
            justifyContent: activeStep === 0 ? "center" : "space-between",
          }}
          className="stepper-navigation-btns"
          mt={4}
        >
          {activeStep !== 0 && (
            <>
              <Button
                variant="outlined"
                disabled={activeStep === 0}
                onClick={handleBack}
                className={classes.button}
              >
                Back
              </Button>
              <Box sx={{ flex: "1 1 auto" }} />          </>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={
              activeStep === stepsLength - 1 ? handleSubmit : handleNextWithSave
            }
            className={classes.button}
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : activeStep === stepsLength - 1 ? (
              "Save"
            ) : (
              "Save & Next"
            )}
          </Button>
        </Box>
      }
    </>
  );
};

export default DocumentDeliverables;
